// components/TaskManager.js
import React, { useState, useEffect } from 'react';
import { db } from '../firebase';
import { doc, setDoc } from 'firebase/firestore';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

const toTitleCase = (str) => str.replace(/(^|\s)\S/g, c => c.toUpperCase());

export default function TaskManager({ user, currentEvent, onEventUpdate }) {
  const { t } = useTranslation();
  const [services, setServices] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [serviceName, setServiceName] = useState('');
  const [serviceCost, setServiceCost] = useState('');
  const [taskTitle, setTaskTitle] = useState('');
  const [taskNotes, setTaskNotes] = useState('');
  const [editingServiceId, setEditingServiceId] = useState(null);
  const [editServiceData, setEditServiceData] = useState({});
  const [editingTaskId, setEditingTaskId] = useState(null);
  const [editTaskData, setEditTaskData] = useState({});
  const [saving, setSaving] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [serviceToDelete, setServiceToDelete] = useState(null);

  const canEdit = () => {
    if (!currentEvent) return false;
    if (!currentEvent.isShared) return true; // Owner can always edit
    return currentEvent.userRole === 'operator';
  };

  useEffect(() => {
    if (currentEvent) {
      setServices(currentEvent.services || []);
      setTasks(currentEvent.tasks || []);
    }
  }, [currentEvent]);

  const saveToFirebase = async (newServices, newTasks) => {
    if (!user || !currentEvent) return;
    setSaving(true);
    try {
      // For shared events, update the owner's document
      const ownerId = currentEvent.ownerId || user.uid;
      const eventRef = doc(db, 'users', ownerId, 'events', currentEvent.id);
      await setDoc(eventRef, { services: newServices, tasks: newTasks }, { merge: true });
      setServices(newServices);
      setTasks(newTasks);
      onEventUpdate({ ...currentEvent, services: newServices, tasks: newTasks });
    } catch (error) {
      console.error('Error saving:', error);
      alert(`${t('tasks.save_error')}\n\n${error.code || ''} ${error.message || error}`);
    } finally {
      setSaving(false);
    }
  };

  const addService = () => {
    if (!serviceName.trim() || !serviceCost) return;
    const newService = { id: Date.now(), name: serviceName, cost: parseFloat(serviceCost), paid: false };
    saveToFirebase([newService, ...services], tasks);
    setServiceName('');
    setServiceCost('');
  };

  const deleteService = (id) => {
    setServiceToDelete(id);
    setShowDeleteConfirm(true);
  };

  const confirmDeleteService = async () => {
    if (serviceToDelete) {
      await saveToFirebase(services.filter(s => s.id !== serviceToDelete), tasks);
      setShowDeleteConfirm(false);
      setServiceToDelete(null);
    }
  };

  const toggleServicePaid = (id) => {
    const updated = services.map(s => s.id === id ? { ...s, paid: !s.paid } : s);
    saveToFirebase(updated, tasks);
  };

  const startEditService = (service) => {
    setEditingServiceId(service.id);
    setEditServiceData({ name: service.name, cost: service.cost });
  };

  const saveEditService = () => {
    const updated = services.map(s => s.id === editingServiceId ? { ...s, name: editServiceData.name, cost: parseFloat(editServiceData.cost) } : s);
    saveToFirebase(updated, tasks);
    setEditingServiceId(null);
    setEditServiceData({});
  };

  const cancelEditService = () => {
    setEditingServiceId(null);
    setEditServiceData({});
  };

  const addTask = () => {
    if (!taskTitle.trim()) return;
    const newTask = { id: Date.now(), title: taskTitle, notes: taskNotes, completed: false };
    saveToFirebase(services, [newTask, ...tasks]);
    setTaskTitle('');
    setTaskNotes('');
  };

  const deleteTask = (id) => {
    saveToFirebase(services, tasks.filter(t => t.id !== id));
  };

  const toggleTaskComplete = (id) => {
    const updated = tasks.map(t => t.id === id ? { ...t, completed: !t.completed } : t);
    saveToFirebase(services, updated);
  };

  const startEditTask = (task) => {
    setEditingTaskId(task.id);
    setEditTaskData({ title: task.title, notes: task.notes || '' });
  };

  const saveEditTask = () => {
    const updated = tasks.map(t => t.id === editingTaskId ? { ...t, ...editTaskData } : t);
    saveToFirebase(services, updated);
    setEditingTaskId(null);
    setEditTaskData({});
  };

  const cancelEditTask = () => {
    setEditingTaskId(null);
    setEditTaskData({});
  };

  const totalExpenses = services.reduce((sum, s) => sum + s.cost, 0);

  if (!currentEvent) {
    return (
      <div className="card shadow-sm">
        <div className="card-body text-center py-5">
          <div className="fs-1">⚠️</div>
          <h4>{t('tasks.no_event_selected')}</h4>
          <p>{t('tasks.select_event_instruction')}</p>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="card shadow-sm mb-4">
        <div className="card-header bg-white fw-bold text-danger">
          🛠️ {t('tasks.services_expenses')} - {currentEvent.name}
          {saving && <span className="badge bg-secondary ms-2">{t('tasks.saving')}</span>}
        </div>
        <div className="card-body">
          <div className="row g-2 mb-3 align-items-end">
            <div className="col-12 col-md-5">
              <label className="form-label small text-muted mb-1">🛠️ {t('tasks.service_name')}</label>
              <input className="form-control" value={serviceName} onChange={e => setServiceName(toTitleCase(e.target.value))} disabled={!canEdit()} />
            </div>
            <div className="col-6 col-md-3">
              <label className="form-label small text-muted mb-1">💶 {t('tasks.service_cost')}</label>
              <input className="form-control" type="number" placeholder="0" value={serviceCost} onChange={e => setServiceCost(e.target.value)} disabled={!canEdit()} />
            </div>
            <div className="col-6 col-md-4">
              <button className="btn btn-danger w-100" onClick={addService} disabled={!canEdit() || saving}>
                <i className="fas fa-plus me-1"></i>{t('tasks.add_service')}
              </button>
            </div>
          </div>
          <div className="table-responsive">
            <table className="table table-sm">
              <thead><tr><th>{t('tasks.service_name')}</th><th>{t('tasks.service_cost')}</th><th>{t('tasks.status')}</th><th>{t('guests.actions')}</th></tr></thead>
              <tbody>
                {services.map(s => (
                  <tr key={s.id}>
                    {editingServiceId === s.id ? (
                      <>
                        <td><input className="form-control form-control-sm" value={editServiceData.name} onChange={e => setEditServiceData({ ...editServiceData, name: toTitleCase(e.target.value) })} /></td>
                        <td><input className="form-control form-control-sm" type="number" value={editServiceData.cost} onChange={e => setEditServiceData({ ...editServiceData, cost: e.target.value })} /></td>
                        <td></td>
                        <td style={{ whiteSpace: 'nowrap' }}>
                          <button className="btn btn-sm btn-success me-1" style={{ padding: '0.15rem 0.4rem' }} onClick={saveEditService} disabled={saving}>
                            <i className="fas fa-check" style={{ fontSize: '0.75rem' }}></i>
                          </button>
                          <button className="btn btn-sm btn-outline-secondary" style={{ padding: '0.15rem 0.4rem' }} onClick={cancelEditService}>
                            <i className="fas fa-times" style={{ fontSize: '0.75rem' }}></i>
                          </button>
                        </td>
                      </>
                    ) : (
                      <>
                        <td>{s.name}</td>
                        <td className="text-danger">€{s.cost}</td>
                        <td><button className={`btn btn-sm ${s.paid ? 'btn-success' : 'btn-warning'}`} style={{ padding: '0.15rem 0.4rem' }} onClick={() => toggleServicePaid(s.id)} disabled={!canEdit()}>{s.paid ? '✅' : '⏳'}</button></td>
                        <td style={{ whiteSpace: 'nowrap' }}>
                          <button className="btn btn-sm btn-outline-primary me-1" style={{ padding: '0.15rem 0.4rem' }} onClick={() => startEditService(s)} disabled={!canEdit()}>
                            <i className="fas fa-edit" style={{ fontSize: '0.75rem' }}></i>
                          </button>
                          <button className="btn btn-sm btn-outline-danger" style={{ padding: '0.15rem 0.4rem' }} onClick={() => deleteService(s.id)} disabled={!canEdit()}>
                            <i className="fas fa-trash" style={{ fontSize: '0.75rem' }}></i>
                          </button>
                        </td>
                      </>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="text-center"><strong>{t('tasks.total_expenses_label')}: €{totalExpenses.toFixed(2)}</strong></div>
        </div>
      </div>

      <div className="card shadow-sm">
        <div className="card-header bg-white fw-bold">📋 {t('tasks.tasks_preparations')}</div>
        <div className="card-body">
          <div className="row g-2 mb-3 align-items-end">
            <div className="col-12 col-md-8">
              <label className="form-label small text-muted mb-1">📋 {t('tasks.task_placeholder')}</label>
              <input className="form-control" value={taskTitle} onChange={e => setTaskTitle(toTitleCase(e.target.value))} disabled={!canEdit()} />
            </div>
            <div className="col-12 col-md-4">
              <button className="btn btn-primary w-100" onClick={addTask} disabled={!canEdit() || saving}>
                <i className="fas fa-plus me-1"></i>{t('tasks.add_task_btn')}
              </button>
            </div>
          </div>
          <div className="list-group">
            {tasks.map(t => (
              <div key={t.id} className="list-group-item">
                {editingTaskId === t.id ? (
                  <div className="row g-2 align-items-end">
                    <div className="col-12 col-md-6">
                      <label className="form-label small text-muted mb-1">📋 {t('tasks.task_placeholder')}</label>
                      <input className="form-control form-control-sm" value={editTaskData.title} onChange={e => setEditTaskData({ ...editTaskData, title: toTitleCase(e.target.value) })} />
                    </div>
                    <div className="col-12 col-md-4">
                      <label className="form-label small text-muted mb-1">📝 {t('tasks.task_notes')}</label>
                      <input className="form-control form-control-sm" value={editTaskData.notes} onChange={e => setEditTaskData({ ...editTaskData, notes: e.target.value })} />
                    </div>
                    <div className="col-12 col-md-2 d-flex gap-1">
                      <button className="btn btn-sm btn-success flex-fill" onClick={saveEditTask} disabled={saving}>
                        <i className="fas fa-check"></i>
                      </button>
                      <button className="btn btn-sm btn-outline-secondary flex-fill" onClick={cancelEditTask}>
                        <i className="fas fa-times"></i>
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="d-flex justify-content-between align-items-center">
                    <div>
                      <input type="checkbox" className="form-check-input me-2" checked={t.completed} onChange={() => toggleTaskComplete(t.id)} disabled={!canEdit()} />
                      <span style={{ textDecoration: t.completed ? 'line-through' : 'none' }}>{t.title}</span>
                      {t.notes && <small className="text-muted d-block ms-4">{t.notes}</small>}
                    </div>
                    <div style={{ whiteSpace: 'nowrap' }}>
                      <button className="btn btn-sm btn-outline-primary me-1" style={{ padding: '0.15rem 0.4rem' }} onClick={() => startEditTask(t)} disabled={!canEdit()}>
                        <i className="fas fa-edit" style={{ fontSize: '0.75rem' }}></i>
                      </button>
                      <button className="btn btn-sm btn-outline-danger" style={{ padding: '0.15rem 0.4rem' }} onClick={() => deleteTask(t.id)} disabled={!canEdit()}>
                        <i className="fas fa-trash" style={{ fontSize: '0.75rem' }}></i>
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(5px)' }} onClick={() => setShowDeleteConfirm(false)}>
          <div className="modal-dialog modal-dialog-centered" onClick={(e) => e.stopPropagation()}>
            <div className="modal-content border-0 shadow-lg" style={{ borderRadius: '20px' }}>
              <div className="modal-header border-0 pb-0">
                <div className="text-center w-100">
                  <div className="fs-1 text-danger mb-2">
                    <i className="fas fa-trash-alt"></i>
                  </div>
                  <h4 className="modal-title fw-bold text-danger">{t('tasks.delete_service')}</h4>
                </div>
                <button type="button" className="btn-close" onClick={() => setShowDeleteConfirm(false)}></button>
              </div>
              <div className="modal-body pt-3 text-center">
                <p className="text-muted mb-0">
                  {t('tasks.delete_service_confirm')}
                </p>
              </div>
              <div className="modal-footer border-0 pt-0">
                <button type="button" className="btn btn-outline-secondary rounded-pill px-4" onClick={() => setShowDeleteConfirm(false)}>
                  <i className="fas fa-times me-1"></i>
                  {t('common.cancel')}
                </button>
                <button type="button" className="btn btn-danger rounded-pill px-4" onClick={confirmDeleteService} disabled={saving}>
                  <i className="fas fa-trash-alt me-1"></i>
                  {saving ? t('tasks.deleting') : t('tasks.delete')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}